import { type ReactElement, useState } from "react";

export type ToolCallStatus = "pending" | "ok" | "error";

export type ToolChipData = {
  toolCallId: string;
  tool: string;
  status: ToolCallStatus;
  inputSummary: string;
  summary?: string | null;
  errorMessage?: string | null;
  durationMs?: number;
  resultPayload?: Record<string, unknown> | null;
};

type Cell = string | number | boolean | null | undefined;

function renderOutput(payload: Record<string, unknown>): ReactElement {
  const type = payload.type as string | undefined;
  if (type === "text" && typeof payload.content === "string") {
    return <pre className="tool-chip-pre">{payload.content}</pre>;
  }
  const columns =
    (payload.columns as Array<{ name: string }> | undefined) ??
    (payload.schema as Array<{ name: string }> | undefined);
  const rows = (payload.rows as Cell[][] | undefined) ?? (payload.sample as Cell[][] | undefined);
  if (columns && rows) {
    return (
      <div className="tool-chip-table-wrap">
        <table className="tool-chip-table">
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={i}>{c.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((cell, j) => (
                  <td key={j}>{cell === null || cell === undefined ? "" : String(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return <pre className="tool-chip-pre">{JSON.stringify(payload, null, 2)}</pre>;
}

const formatDuration = (ms: number | undefined): string => {
  if (!ms || ms < 1) return "";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const statusText = (d: ToolChipData): string => {
  if (d.status === "pending") return "running";
  if (d.status === "error") return d.errorMessage || d.summary || "failed";
  return d.summary || "ok";
};

export function ToolChip({ data }: { data: ToolChipData }) {
  const [open, setOpen] = useState(false);
  const duration = formatDuration(data.durationMs);
  const summaryLine = statusText(data);
  const cls = `tool-chip tool-chip--${data.status}`;
  return (
    <div className={cls}>
      <button
        type="button"
        className="tool-chip-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="tool-chip-icon" aria-hidden="true">
          🔧
        </span>
        <span className="tool-chip-title">
          {data.status === "pending" ? "Running" : "Ran"} <code>{data.tool}</code>
        </span>
        <span className="tool-chip-meta">
          {duration && <span>{duration}</span>}
          {duration && summaryLine && <span aria-hidden="true">·</span>}
          {summaryLine && <span>{summaryLine}</span>}
        </span>
        <span className="tool-chip-chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open && (
        <div className="tool-chip-body">
          <div className="tool-chip-section">
            <div className="tool-chip-label">Input</div>
            <pre className="tool-chip-pre">{data.inputSummary}</pre>
          </div>
          {data.status === "error" && data.errorMessage && (
            <div className="tool-chip-section">
              <div className="tool-chip-label">Error</div>
              <pre className="tool-chip-pre tool-chip-pre--error">{data.errorMessage}</pre>
            </div>
          )}
          {data.status === "ok" && data.resultPayload && (
            <div className="tool-chip-section">
              <div className="tool-chip-label">Output</div>
              {renderOutput(data.resultPayload)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
